import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  alpha,
  CircularProgress,
  IconButton,
  Popover,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Tooltip,
  Alert
} from '@mui/material';
import {
  Description as ResumeIcon,
  Send as SendIcon,
  People as InterviewIcon,
  FactCheck as RefCheckIcon,
  Handshake as OfferIcon,
  Groups as MeetingIcon,
  FreeBreakfast as BreakIcon,
  School as TrainingIcon,
  Task as AdminIcon,
  Add as AddIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import CallAccountContactPicker from './CallAccountContactPicker';
import { parseHmsToSeconds } from '../../utils/breakDurationHms';
import { setBreakSession } from '../../utils/breakSessionStorage';

const activityIcons = {
  call_candidate: PhoneIcon,
  call_client: PhoneIcon,
  call_vendor: PhoneIcon,
  send_email: EmailIcon,
  screen_resume: ResumeIcon,
  submit_resume: SendIcon,
  interview: InterviewIcon,
  reference_check: RefCheckIcon,
  offer_discussion: OfferIcon,
  meeting: MeetingIcon,
  break: BreakIcon,
  training: TrainingIcon,
  admin_task: AdminIcon
};

const activityColors = {
  call_candidate: '#4CAF50',
  call_client: '#2196F3',
  call_vendor: '#E65100',
  send_email: '#9C27B0',
  screen_resume: '#FF9800',
  submit_resume: '#00BCD4',
  interview: '#E91E63',
  reference_check: '#795548',
  offer_discussion: '#607D8B',
  meeting: '#3F51B5',
  break: '#8BC34A',
  training: '#673AB7',
  admin_task: '#9E9E9E'
};

const activityPlaceholders = {
  call_candidate: 'e.g., Called John Doe about Java Developer position, discussed availability',
  call_client: 'e.g., Discussed headcount, status update',
  call_vendor: 'e.g., Tooling renewal, support ticket',
  send_email: 'e.g., Sent job description to 5 candidates for React position',
  screen_resume: 'e.g., Reviewed 10 resumes for Data Analyst role',
  submit_resume: 'e.g., Submitted Mike Smith to XYZ Inc for DevOps Engineer',
  interview: 'e.g., Conducted phone screening with Sarah for PM role',
  reference_check: 'e.g., Called previous employer for John - positive feedback',
  offer_discussion: 'e.g., Discussed salary expectations with candidate - $120K',
  meeting: 'e.g., Team standup - discussed open requirements',
  break: 'e.g., Lunch break',
  training: 'e.g., Completed ATS training module',
  admin_task: 'e.g., Updated candidate database records'
};

const isCallClientOrVendor = (code) => code === 'call_client' || code === 'call_vendor';

const needsContactName = (code) =>
  [
    'call_candidate',
    'send_email',
    'interview',
    'submit_resume',
    'reference_check',
    'offer_discussion'
  ].includes(code);

/**
 * “Log activity” in the app bar. Interactive only when punched in (`canLogActivity`).
 * When punched out, the control stays visible but disabled with a short hint to punch in first.
 */
const LogActivityHeaderButton = ({ canLogActivity = true }) => {
  const [activityTypes, setActivityTypes] = useState([]);
  const [activityAnchor, setActivityAnchor] = useState(null);
  const [loggingActivity, setLoggingActivity] = useState(false);
  const [activityDialog, setActivityDialog] = useState({ open: false, activity: null });
  const [activityNotes, setActivityNotes] = useState('');
  const [contactName, setContactName] = useState('');
  const [contacts, setContacts] = useState([]);
  const [callPick, setCallPick] = useState({ account: null, contact: null });
  const [breakDurationHms, setBreakDurationHms] = useState('00:15:00');

  const fetchActivityTypes = useCallback(async () => {
    try {
      const response = await api.get('/activities/quick-types');
      setActivityTypes(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch activity types:', error);
    }
  }, []);

  const fetchContacts = useCallback(async () => {
    try {
      const response = await api.get('/contacts?limit=100&status=active');
      setContacts(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    }
  }, []);

  useEffect(() => {
    fetchActivityTypes();
    fetchContacts();
  }, [fetchActivityTypes, fetchContacts]);

  useEffect(() => {
    if (!canLogActivity) {
      setActivityAnchor(null);
      setActivityDialog({ open: false, activity: null });
      setCallPick({ account: null, contact: null });
      setActivityNotes('');
      setContactName('');
    }
  }, [canLogActivity]);

  const handleActivityClick = (activityType) => {
    setActivityDialog({ open: true, activity: activityType });
    setActivityNotes('');
    setContactName('');
    setCallPick({ account: null, contact: null });
    setActivityAnchor(null);
    if (activityType?.code === 'break') {
      setBreakDurationHms('00:15:00');
    }
  };

  const handleSubmitActivity = async () => {
    if (!canLogActivity || !activityDialog.activity) return;
    const code = activityDialog.activity.code;
    if (code === 'call_client' || code === 'call_vendor') {
      if (!callPick.account || !callPick.contact) {
        toast.error('Select the company, then the contact you called.');
        return;
      }
    }
    if (code === 'break') {
      const sec = parseHmsToSeconds(breakDurationHms);
      if (sec == null) {
        toast.error('Enter break length as digital HH:MM:SS (e.g. 00:15:00).');
        return;
      }
      if (!String(activityNotes).trim()) {
        toast.error('Notes are required for a break.');
        return;
      }
    }
    setLoggingActivity(true);
    const label = activityDialog.activity.label;
    try {
      if (code === 'break') {
        const sec = parseHmsToSeconds(breakDurationHms);
        const breakStartedAt = Date.now();
        const until = breakStartedAt + sec * 1000;
        try {
          await api.post('/punchcard/punch-out');
        } catch (poErr) {
          console.error(poErr);
          toast.error(poErr.response?.data?.message || 'Could not punch out for break.');
          return;
        }
        await api.post('/activities/quick', {
          activity_type: 'break',
          notes: String(activityNotes).trim(),
          duration_minutes: Math.max(1, Math.ceil(sec / 60)),
          break_duration_hms: String(breakDurationHms).trim(),
          break_duration_seconds: sec,
          break_planned_end_at: new Date(until).toISOString()
        });
        setBreakSession({ untilMs: until, plannedSeconds: sec, startedAtMs: breakStartedAt });
        window.dispatchEvent(new CustomEvent('erp4cBreakSessionStarted'));
        window.dispatchEvent(new CustomEvent('punchStatusChanged', { detail: { action: 'break-started' } }));
        setActivityDialog({ open: false, activity: null });
        setCallPick({ account: null, contact: null });
        setActivityNotes('');
        setContactName('');
        toast.success('Break started — you are punched out. Use the break timer when it appears.');
        return;
      }

      const notesWithContact = contactName
        ? `${contactName}: ${activityNotes}`.trim()
        : activityNotes;

      const body = {
        activity_type: code,
        notes: (code === 'call_client' || code === 'call_vendor' ? activityNotes : notesWithContact) || null,
        duration_minutes: null
      };
      if (code === 'call_client' || code === 'call_vendor') {
        body.account_id = callPick.account.id;
        body.contact_id = callPick.contact.id;
      }

      await api.post('/activities/quick', body);
      window.dispatchEvent(
        new CustomEvent('punchStatusChanged', { detail: { action: 'activity-logged' } })
      );
      setActivityDialog({ open: false, activity: null });
      setCallPick({ account: null, contact: null });
      setActivityNotes('');
      setContactName('');
      toast.success(`${label} logged successfully!`);
    } catch (error) {
      console.error('Failed to log activity:', error);
      toast.error(error.response?.data?.message || 'Failed to log activity.');
    } finally {
      setLoggingActivity(false);
    }
  };

  const closeDialog = () => {
    setActivityDialog({ open: false, activity: null });
    setCallPick({ account: null, contact: null });
    setActivityNotes('');
    setContactName('');
    setBreakDurationHms('00:15:00');
  };

  const buttonSxEnabled = {
    flexShrink: 0,
    textTransform: 'none',
    fontWeight: 700,
    fontSize: { xs: '0.9rem', sm: '0.95rem' },
    py: 0.75,
    px: 1.75,
    minHeight: 40,
    boxShadow: (theme) => `0 2px 8px ${alpha(theme.palette.primary.main, 0.45)}`,
    border: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
    '&:hover': {
      boxShadow: (theme) => `0 4px 12px ${alpha(theme.palette.primary.dark, 0.5)}`
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: canLogActivity ? 'center' : 'flex-start', sm: 'center' },
          flexDirection: { xs: canLogActivity ? 'row' : 'column', sm: 'row' },
          gap: { xs: 0.5, sm: 1.25 },
          flexWrap: 'wrap',
          minWidth: 0
        }}
      >
        <Tooltip
          title={
            canLogActivity
              ? 'Log calls, emails, meetings, and other work'
              : 'You need to punch in to log activity.'
          }
        >
          <span>
            <Button
              color="primary"
              variant="contained"
              disabled={!canLogActivity}
              onClick={canLogActivity ? (e) => setActivityAnchor(e.currentTarget) : undefined}
              startIcon={<AddIcon sx={{ fontSize: 22 }} />}
              sx={
                canLogActivity
                  ? buttonSxEnabled
                  : {
                      flexShrink: 0,
                      textTransform: 'none',
                      fontWeight: 600,
                      fontSize: { xs: '0.8rem', sm: '0.8rem' },
                      py: 0.5,
                      px: 1.5,
                      minHeight: 34
                    }
              }
            >
              Log activity
            </Button>
          </span>
        </Tooltip>
        {!canLogActivity && (
          <Typography
            component="span"
            variant="caption"
            sx={{
              /* Works on light (#fff) and dark App bar — theme `text` tokens */
              color: 'text.secondary',
              maxWidth: { xs: 280, sm: 320, md: 360 },
              lineHeight: 1.35,
              pl: { xs: 0.25, sm: 0 }
            }}
          >
            You need to punch in to log activity.
          </Typography>
        )}
      </Box>

      <Popover
        open={canLogActivity && Boolean(activityAnchor)}
        anchorEl={activityAnchor}
        onClose={() => setActivityAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            maxWidth: 'min(100vw - 32px, 600px)',
            minWidth: { xs: 320, sm: 480 },
            borderRadius: 2
          }
        }}
      >
        <Box sx={{ p: 2.5, minWidth: 300 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 700, color: 'text.secondary', display: 'block', mb: 1.5, fontSize: '0.95rem' }}
          >
            Log external activity
          </Typography>
          <Grid container spacing={1.25}>
            {activityTypes.map((activity) => {
              const IconComponent = activityIcons[activity.code] || AdminIcon;
              return (
                <Grid item xs={6} sm={4} key={activity.code}>
                  <Button
                    fullWidth
                    size="medium"
                    variant="outlined"
                    startIcon={
                      <IconComponent sx={{ fontSize: 22, color: activityColors[activity.code] || 'inherit' }} />
                    }
                    onClick={() => handleActivityClick(activity)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      color: 'text.primary',
                      fontSize: { xs: '0.8rem', sm: '0.9rem' },
                      fontWeight: 600,
                      py: 1.1,
                      px: 1.25,
                      borderColor: (theme) => alpha(activityColors[activity.code] || theme.palette.divider, 0.45),
                      '&:hover': {
                        borderColor: activityColors[activity.code] || 'grey.400',
                        bgcolor: alpha(activityColors[activity.code] || '#9E9E9E', 0.1)
                      }
                    }}
                  >
                    {activity.label}
                  </Button>
                </Grid>
              );
            })}
          </Grid>
        </Box>
      </Popover>

      <Dialog
        open={canLogActivity && activityDialog.open}
        onClose={closeDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2.5, px: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            {activityDialog.activity && (
              <>
                {React.createElement(activityIcons[activityDialog.activity.code] || AdminIcon, {
                  sx: {
                    fontSize: 40,
                    color: activityColors[activityDialog.activity.code]
                  }
                })}
                <Typography variant="h5" component="span" sx={{ fontWeight: 700 }}>
                  {activityDialog.activity?.label}
                </Typography>
              </>
            )}
          </Box>
          <IconButton
            onClick={closeDialog}
            size="small"
            aria-label="Close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 3, py: 2, '& .MuiInputBase-root': { fontSize: '1rem' } }}>
          {activityDialog.activity?.code === 'break' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
              <Alert severity="warning">
                Your break window starts <strong>when you submit</strong> this form. The system will <strong>punch you
                out</strong> for the break.
              </Alert>
              <Alert severity="info">
                When you return, <strong>punch in</strong> (or use another flow that opens a work session). Try to be back
                a few seconds <strong>before</strong> the timer hits zero so you can punch in on time.
              </Alert>
              <TextField
                label="Break length (HH:MM:SS)"
                fullWidth
                value={breakDurationHms}
                onChange={(e) => setBreakDurationHms(e.target.value)}
                placeholder="00:15:00"
                helperText="Digital 24h style, max 23:59:59. Example: 00:30:00 = 30 minutes."
                inputProps={{ spellCheck: false, sx: { fontFamily: 'monospace', fontSize: '1.1rem' } }}
              />
            </Box>
          )}
          {activityDialog.activity && isCallClientOrVendor(activityDialog.activity.code) && (
            <CallAccountContactPicker
              key={activityDialog.activity.code}
              scope={activityDialog.activity.code === 'call_vendor' ? 'vendor' : 'client'}
              valueAccount={callPick.account}
              valueContact={callPick.contact}
              onChange={(acc, c) => setCallPick({ account: acc, contact: c })}
              disabled={loggingActivity}
            />
          )}
          {activityDialog.activity && needsContactName(activityDialog.activity.code) && (
            <Autocomplete
              freeSolo
              options={contacts.map((c) => `${c.first_name} ${c.last_name}`)}
              value={contactName}
              onChange={(e, value) => setContactName(value || '')}
              onInputChange={(e, value) => setContactName(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Contact / candidate name"
                  placeholder="Type or select name…"
                  fullWidth
                  sx={{ mb: 2 }}
                />
              )}
            />
          )}
          <TextField
            label={activityDialog.activity?.code === 'break' ? 'Notes (required)' : 'Notes / details'}
            required={activityDialog.activity?.code === 'break'}
            placeholder={activityPlaceholders[activityDialog.activity?.code] || 'Add details about this activity...'}
            multiline
            minRows={4}
            fullWidth
            value={activityNotes}
            onChange={(e) => setActivityNotes(e.target.value)}
            helperText={
              activityDialog.activity?.code === 'break'
                ? 'Required for every break. Describe where you are going / context.'
                : 'What did you do? Who was involved? What was the outcome?'
            }
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2.5 }}>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button
            color="primary"
            variant="contained"
            onClick={handleSubmitActivity}
            disabled={
              loggingActivity ||
              (activityDialog.activity &&
                isCallClientOrVendor(activityDialog.activity.code) &&
                (!callPick.account || !callPick.contact)) ||
              (activityDialog.activity?.code === 'break' && !String(activityNotes).trim())
            }
            startIcon={loggingActivity ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {loggingActivity ? 'Logging…' : activityDialog.activity?.code === 'break' ? 'Start break' : 'Log activity'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LogActivityHeaderButton;
